/**
 * Per-user delivery preferences that live on `users/{uid}` rather than on the
 * family document.
 *
 * Quiet hours are a property of a PERSON, not of a family. Co-parents in
 * different timezones are common in this product, and a parent who works nights
 * has a different quiet window from one who doesn't. Storing this on the family
 * doc (next to parentProfiles) would force one shared window on both.
 *
 * It also has to live where the Cloud Functions already look: `notifyFamily` in
 * functions/expoPush.js reads each recipient's `users/{uid}` doc for their push
 * token and preferences, so quiet hours cost it no extra read.
 */

import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { onSnapshotWithRetry as onSnapshot } from '../utils/firestoreSubscribe';
import { getDb } from '../firebase';

/**
 * Subscribe to another user's delivery preferences.
 *
 * Used by the composer to tell whether the co-parent is currently inside their
 * quiet window. Firestore rules allow any signed-in user to read user docs, and
 * only the two fields relevant to delivery are surfaced here — callers get no
 * reason to reach for the rest of the document.
 *
 * @param {string} userId
 * @param {function({quietHours: Object|null, timezone: string|null}): void} callback
 * @returns {function} unsubscribe
 */
export const subscribeToDeliveryPrefs = (userId, callback) => {
  const db = getDb();
  return onSnapshot(
    doc(db, 'users', userId),
    (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : null;
      callback({
        quietHours: data?.quietHours || null,
        timezone: data?.timezone || null,
      });
    },
    (error) => {
      console.error('Delivery prefs listener error:', error);
      callback({ quietHours: null, timezone: null });
    }
  );
};

/** One-shot read of a user's delivery preferences. */
export const getDeliveryPrefs = async (userId) => {
  const db = getDb();
  try {
    const snapshot = await getDoc(doc(db, 'users', userId));
    const data = snapshot.exists() ? snapshot.data() : null;
    return {
      quietHours: data?.quietHours || null,
      timezone: data?.timezone || null,
    };
  } catch (error) {
    console.error('Error fetching delivery prefs:', error);
    return { quietHours: null, timezone: null };
  }
};

/**
 * Save the signed-in user's quiet hours.
 *
 * Written as a whole object rather than dotted paths: unlike notificationPrefs
 * — where a full-map assignment has already silently dropped keys a screen
 * didn't render — quietHours is a single closed shape with exactly three
 * fields, all of which the settings UI owns.
 *
 * @param {string} userId
 * @param {{enabled: boolean, startHour: number, endHour: number}} quietHours
 */
export const updateQuietHours = async (userId, quietHours) => {
  const db = getDb();
  await updateDoc(doc(db, 'users', userId), {
    quietHours: {
      enabled: quietHours.enabled === true,
      startHour: quietHours.startHour,
      endHour: quietHours.endHour,
    },
  });
};
